import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Alert,
  Spinner,
  ListGroup,
  ListGroupItem
} from 'reactstrap';
import {
  FaPlus,
  FaMinus,
  FaSave,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoice,
  FaTrash,
  FaEdit,
  FaSearch,
  FaBarcode,
  FaCalculator,
  FaTruck,
  FaInfoCircle,
  FaCheck,
  FaMoneyBill,
  FaFileAlt,
  FaUniversity,
  FaCreditCard,
  FaFileContract,
  FaQuestion
} from 'react-icons/fa';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Select from 'react-select/async';
import { components } from 'react-select';
import { debounce } from 'lodash';

// Redux actions
import { clientActions } from '../../sagas/clientSlice';
import { productActions } from '../../sagas/productSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';

// Utilities
import { formatPrice } from '../../helpers/utility';
import ClientForm from './ClientForm';
import { reglementActions } from '../../sagas/reglementSlice';
import { useLocation } from 'react-router-dom';

// Validation schema
const validationSchema = Yup.object({
  numero: Yup.string().required('Numéro de facture requis'),
  dateFacture: Yup.date().required('Date de facture requise'),
  dateEcheance: Yup.date()
    .min(Yup.ref('dateFacture'), 'La date d\'échéance doit être postérieure à la date de facture'),
  clientId: Yup.number().required('Client requis'),
  modeReglement: Yup.string()
    .required('Mode de règlement requis')
    .oneOf(['ESPECES', 'CHEQUE', 'VIREMENT', 'TRAITE', 'CARTE_BANCAIRE'], 'Mode de règlement invalide'),

  // Validation conditionnelle corrigée pour numeroCheque
  numeroCheque: Yup.string().when('modeReglement', {
    is: 'CHEQUE',
    then: (schema) => schema.required('Numéro de chèque requis'),
    otherwise: (schema) => schema.notRequired()
  }),

  // Validation conditionnelle corrigée pour banque
  banque: Yup.string().when('modeReglement', {
    is: 'CHEQUE',
    then: (schema) => schema.required('Nom de la banque requis'),
    otherwise: (schema) => schema.notRequired()
  }),

  // Validation conditionnelle pour dateEcheanceCheque
  dateEcheanceCheque: Yup.date().when('modeReglement', {
    is: 'CHEQUE',
    then: (schema) => schema
      .required('Date d\'échéance du chèque requise')
      .min(new Date(), 'La date d\'échéance du chèque ne peut pas être dans le passé'),
    otherwise: (schema) => schema.notRequired()
  }),

  lignes: Yup.array()
    .min(1, 'Au moins un article est requis')
    .of(
      Yup.object({
        articleId: Yup.number().required(),
        quantite: Yup.number().min(0.01, 'Quantité doit être supérieure à 0').required(),
        prixUnitaireHT: Yup.number().min(0, 'Prix unitaire doit être positif').required(),
        tauxTVA: Yup.number().min(0, 'Taux TVA doit être positif').max(1, 'Taux TVA ne peut pas dépasser 100%')
      })
    )
});

const ViewFacture = ({ invoice = null, onSave, onCancel, isEditing = false, fromPayments = false, selectedPayments = [] }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Redux selectors
  const { loading: invoiceLoading } = useSelector(state => state.invoices);
  const { loading: reglementsLoading } = useSelector(state => state.reglements);
  const { products, loading: productsLoading } = useSelector(state => state.products);
  const { clients } = useSelector(state => state.clients);
  const unites = useSelector(state => state.products.unites);

  const canBeUpdated = isEditing && invoice?.statut !== 'ANNULEE';
  // Local state
  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [showSelectPaymentsModal, setShowSelectPaymentsModal] = useState(false);
  const [availablePayments, setAvailablePayments] = useState([]);
  const [selectedPaymentsForInvoice, setSelectedPaymentsForInvoice] = useState(selectedPayments || []);
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [clientForPayments, setClientForPayments] = useState(null);

  // Refs
  const productSearchRef = useRef(null);

  const location = useLocation();

  // Si les props ne sont pas passées directement, essayez de les récupérer de location.state
  const stateFromPayments = location.state?.fromPayments || fromPayments;
  const stateSelectedPayments = location.state?.selectedPayments || selectedPayments;
  const stateClientId = location.state?.clientId;

  // Ajoutez ces états
  const [paymentsForInvoice, setPaymentsForInvoice] = useState(stateSelectedPayments || []);
  const [showPaymentsDetails, setShowPaymentsDetails] = useState(stateFromPayments);

  // Calculer le total des paiements
  const calculatePaymentsTotal = useCallback(() => {
    return paymentsForInvoice.reduce((sum, payment) => sum + (payment.montantRegle || 0), 0);
  }, [paymentsForInvoice]);


  // Initial values for formik
  const getInitialValues = () => {
    console.log(invoice)
    if (isEditing && invoice) {
      return {
        numero: invoice.numero || '',
        dateFacture: invoice.dateFacture || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEcheance: invoice.dateEcheance || '',
        clientId: invoice.clientId || null,
        clientCode: invoice.clientCode || '',
        clientNom: invoice.clientNom || '',
        clientAdresse: invoice.clientAdresse || '',
        clientTelephone1: invoice.clientTelephone1 || '',
        clientEmail: invoice.clientEmail || '',
        clientIce: invoice.clientIce || '',
        modeReglement: invoice.modeReglement,
        numeroCheque: invoice.numeroCheque || '',
        dateEcheanceCheque: invoice.dateEcheanceCheque,
        banque: invoice.banque || '',
        commentaire: invoice.commentaire || '',
        lignes: invoice.lignes || [],
        montantTotalHT: invoice.montantTotalHT || 0,
        montantTotalTTC: invoice.montantTotalTTC || 0,
        montantTotalTaxe: invoice.montantTotalTaxe || 0,
        montantRegle: invoice.montantRegle || 0,
        statut: invoice.statut || 'VALIDEE'
      };
    }

    // Pour une nouvelle facture
    const initialValues = {
      numero: '',
      dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
      dateEcheance: '',
      clientId: stateClientId || null,
      clientCode: '',
      clientNom: '',
      clientAdresse: '',
      clientTelephone1: '',
      clientEmail: '',
      clientIce: '',
      modeReglement: null,
      numeroCheque: '',
      dateEcheanceCheque: null,
      banque: '',
      commentaire: '',
      lignes: [],
      montantTotalHT: 0,
      montantTotalTTC: 0,
      montantTotalTaxe: 0,
      montantRegle: stateFromPayments ? calculatePaymentsTotal() : 0,
      statut: stateFromPayments ? 'PAYEE' : 'BROUILLON'
    };
    return initialValues;
  };

  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'ESPECES':
        return <FaMoneyBill className="text-success" />;
      case 'CHEQUE':
        return <FaFileAlt className="text-primary" />;
      case 'VIREMENT':
        return <FaUniversity className="text-info" />;
      case 'CARTE_BANCAIRE':
        return <FaCreditCard className="text-dark" />;
      default:
        return <FaQuestion className="text-secondary" />;
    }
  };


  // Formik configuration
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: (values) => {
      handleSaveInvoice(values);
    }
  });

  // Load all payments for a client
  const loadClientPayments = (clientId) => {
    if (!clientId) return;

    dispatch(reglementActions.getReglementsByClientRequest({
      clientId,
      page: 0,
      size: 100,
      onSuccess: (data) => {
        // Filter to only include payments that don't have an associated invoice
        const availableForInvoice = data.content.filter(payment =>
          !payment.allocations ||
          !payment.allocations.some(a => a.documentType === 'FACTURE')
        );
        setAvailablePayments(availableForInvoice);
      },
      onError: (error) => {
        console.error('Error loading client payments:', error);
        setAvailablePayments([]);
      }
    }));
  };


  useEffect(() => {
    if (isEditing && invoice?.id) {
      // Load payments for this invoice
      loadInvoicePayments(invoice.id);
    }

    // If creating from payments, load client data
    if (fromPayments && selectedPaymentsForInvoice.length > 0) {
      const clientId = selectedPaymentsForInvoice[0].clientId;
      if (clientId) {
        dispatch(clientActions.getClientByIdRequest({
          id: clientId,
          onSuccess: (client) => {
            setClientForPayments(client);
            formik.setFieldValue('clientId', client.id);
            formik.setFieldValue('clientNom', client.typeClient === 'PARTICULIER'
              ? `${client.nom} ${client.prenom}`
              : client.entreprise);
            formik.setFieldValue('clientAdresse', client.adresse || '');
            formik.setFieldValue('clientTelephone1', client.telephone1 || '');
            formik.setFieldValue('clientEmail', client.email || '');
            formik.setFieldValue('clientIce', client.ice || '');
          }
        }));
      }
    }
  }, []);

  // Load invoice payments
  const loadInvoicePayments = (id) => {
    if (!id) return;

    dispatch(reglementActions.getReglementsByInvoiceRequest({
      numero: id,
      onSuccess: (data) => {
        setInvoicePayments(data || []);
      },
      onError: (error) => {
        console.error('Error loading payments:', error);
        setInvoicePayments([]);
      }
    }));
  };

  // Load initial data
  useEffect(() => {
    dispatch(productActions.findAllUnites());
    loadClientOptions('');

    if (!isEditing) {
      // Generate new invoice number for new invoices
      dispatch(invoiceActions.fetchNewInvoiceNumber({
        onSuccess: (numero) => {
          formik.setFieldValue('numero', numero);
        }
      }));
    }
  }, []);

  // Load client options
  const loadClientOptions = async (inputValue) => {
    return new Promise((resolve) => {
      dispatch(clientActions.search({
        query: inputValue,
        onSuccess: (data) => {
          const options = data.content.map(client => {
            let label = '';
            if (client.typeClient === 'PARTICULIER') {
              label = (client.code ? client.code + ' - ' : '') + client.nom + ' ' + client.prenom;
            } else {
              label = (client.code ? client.code + ' - ' : '') + client.entreprise;
            }
            return {
              value: client.id,
              label,
              client
            };
          });
          setClientOptions(options);
          resolve(options);
        }
      }));
    });
  };

  // Update line field
  const handleLineFieldChange = (index, field, value) => {
    const updatedLignes = [...formik.values.lignes];
    updatedLignes[index][field] = value;

    // Recalculate amounts when quantity or price changes
    if (field === 'quantite' || field === 'prixUnitaireHT' || field === 'tauxTVA') {
      const ligne = updatedLignes[index];
      ligne.montantHT = ligne.quantite * ligne.prixUnitaireHT;
      ligne.prixUnitaireTTC = ligne.prixUnitaireHT * (1 + ligne.tauxTVA);
      ligne.montantTTC = ligne.quantite * ligne.prixUnitaireTTC;
      ligne.montantTaxe = ligne.montantTTC - ligne.montantHT;
    }

    formik.setFieldValue('lignes', updatedLignes);
  };

  const parseBonLivraisonRefs = (refs) => {
    if (!refs || refs.trim() === '') return [];
    return refs.split(',').map(ref => ref.trim()).filter(ref => ref !== '');
  };

  // Handle save invoice
  const handleSaveInvoice = (values) => {
    if (values.lignes.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('facture.noLines'),
        text: t('facture.noLinesMessage')
      });
      return;
    }

    const invoiceData = {
      ...values,
      lignes: values.lignes.map(ligne => ({
        ...ligne,
        montantHT: parseFloat(ligne.montantHT || 0),
        montantTTC: parseFloat(ligne.montantTTC || 0),
        montantTaxe: parseFloat(ligne.montantTaxe || 0),
        quantite: parseFloat(ligne.quantite || 0),
        prixUnitaireHT: parseFloat(ligne.prixUnitaireHT || 0),
        prixUnitaireTTC: parseFloat(ligne.prixUnitaireTTC || 0),
        tauxTVA: parseFloat(ligne.tauxTVA || 0)
      }))
    };

    if (isEditing && invoice?.id) {
      // Mise à jour d'une facture existante
      dispatch(invoiceActions.updateInvoiceRequest({
        id: invoice.id,
        invoiceData,
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: t('facture.updateSuccess'),
            text: t('facture.updateSuccessMessage'),
            timer: 2000,
            showConfirmButton: false
          });
          if (onSave) {
            onSave();
          }
        },
        onError: (error) => {
          Swal.fire({
            icon: 'error',
            title: t('facture.updateError'),
            text: error.message || t('facture.updateErrorMessage'),
          });
        }
      }));
    } else {
      // Création d'une nouvelle facture
      dispatch(invoiceActions.addInvoice({
        invoice: invoiceData,
        onSuccess: (newInvoice) => {
          Swal.fire({
            icon: 'success',
            title: t('facture.createSuccess'),
            text: t('facture.createSuccessMessage'),
            timer: 2000,
            showConfirmButton: false
          });

          // Si on vient des paiements, lier les paiements à la facture
          if (stateFromPayments && paymentsForInvoice.length > 0) {
            const paymentIds = paymentsForInvoice.map(p => p.id);

            dispatch(reglementActions.linkPaymentsToInvoiceRequest({
              invoiceId: newInvoice.id,
              paymentIds: paymentIds,
              onSuccess: () => {
                Swal.fire({
                  icon: 'success',
                  title: t('facture.paymentsLinkedSuccess'),
                  text: t('facture.paymentsLinkedSuccessMessage'),
                  timer: 2000,
                  showConfirmButton: false
                });
              },
              onError: (error) => {
                Swal.fire({
                  icon: 'warning',
                  title: t('facture.paymentsLinkedWarning'),
                  text: error.message || t('facture.paymentsLinkedWarningMessage'),
                });
              }
            }));
          }

          if (onSave) {
            onSave(newInvoice);
          }
        },
        onError: (error) => {
          Swal.fire({
            icon: 'error',
            title: t('facture.createError'),
            text: error.message || t('facture.createErrorMessage'),
          });
        }
      }));
    }
  };

  // Handle cancel invoice
  const handleCancelInvoice = () => {
    if (!isEditing || !invoice?.id) return;

    Swal.fire({
      title: t('facture.confirmCancel'),
      text: t('facture.confirmCancelMessage'),
      icon: 'warning',
      input: 'textarea',
      inputLabel: t('facture.cancelReason'),
      inputPlaceholder: t('facture.enterCancelReason'),
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return t('facture.cancelReasonRequired');
        }
      },
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('facture.confirmCancelButton'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(invoiceActions.cancelInvoiceRequest({
          id: invoice.id,
          motifAnnulation: result.value,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('facture.cancelSuccess'),
              text: t('facture.cancelSuccessMessage'),
              timer: 2000,
              showConfirmButton: false
            });
            if (onCancel) {
              onCancel();
            }
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('facture.cancelError'),
              text: error.message || t('facture.cancelErrorMessage'),
            });
          }
        }));
      }
    });
  };

  // Get selected client option
  const getSelectedClientOption = () => {
    if (formik.values.clientId) {
      return clientOptions.find(option => option.value === formik.values.clientId);
    }
    return null;
  };

  // Get unit label
  const getUniteLabel = (uniteCode) => {
    const unite = unites.find(u => u.code === uniteCode);
    return unite ? unite.libelle : uniteCode;
  };


  // Get payment mode label
  const getPaymentModeLabel = (mode) => {
    switch (mode) {
      case 'ESPECES': return t('facture.modeReglement.espece');
      case 'CHEQUE': return t('facture.modeReglement.cheque');
      case 'VIREMENT': return t('facture.modeReglement.virement');
      case 'CARTE_BANCAIRE': return t('facture.modeReglement.card');
      default: return mode;
    }
  };

  // Get payment status label
  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'VALIDE': return t('payment.validated');
      case 'EN_ATTENTE': return t('payment.pending');
      case 'ANNULE': return t('payment.cancelled');
      case 'ENCAISSE': return t('payment.cashed');
      case 'REJETE': return t('payment.rejected');
      default: return status;
    }
  };

  // Ajoutez cette fonction pour obtenir le badge du statut
  const getPaymentStatusBadge = (statut) => {
    const colors = {
      'EN_ATTENTE': 'warning',
      'VALIDE': 'info',
      'ENCAISSE': 'success',
      'REJETE': 'danger',
      'ANNULE': 'secondary'
    };

    return (
      <Badge color={colors[statut] || 'secondary'}>
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'VALIDE': return 'success';
      case 'EN_ATTENTE': return 'warning';
      case 'ANNULE': return 'danger';
      case 'ENCAISSE': return 'primary';
      case 'REJETE': return 'danger';
      default: return 'secondary';
    }
  };

  // Calculate total paid amount
  const calculateTotalPaid = () => {
    return invoicePayments.reduce((sum, payment) => sum + (payment.montantAlloue || 0), 0);
  };

  // Calculate remaining amount to pay
  const calculateRemainingAmount = () => {
    const totalPaid = calculateTotalPaid();
    return totalPaid > formik.values.montantTotalTTC ? 0 : formik.values.montantTotalTTC - totalPaid;
  };

  // Ajoutez cette fonction pour supprimer un paiement de la liste
  const handleRemovePayment = (paymentId) => {
    setPaymentsForInvoice(paymentsForInvoice.filter(p => p.id !== paymentId));

    // Recalculer le montant réglé
    const newTotal = paymentsForInvoice.filter(p => p.id !== paymentId)
      .reduce((sum, p) => sum + (p.montantRegle || 0), 0);

    formik.setFieldValue('montantRegle', newTotal);

    // Mettre à jour le statut si nécessaire
    if (newTotal >= formik.values.montantTotalTTC) {
      formik.setFieldValue('statut', 'PAYEE');
    } else if (newTotal > 0) {
      formik.setFieldValue('statut', 'PARTIELLEMENT_PAYEE');
    } else {
      formik.setFieldValue('statut', 'BROUILLON');
    }
  };


  // Calculate total amount from selected payments
  const calculateSelectedPaymentsTotal = () => {
    return selectedPaymentsForInvoice.reduce((sum, payment) => sum + (payment.montantRegle || 0), 0);
  };


  return (
    <Container fluid>
      <Form onSubmit={formik.handleSubmit}>
        {/* <Row className="mb-3">
          <Col>
            {renderSelectPaymentsButton()}
          </Col>
        </Row> */}
        <Row>
          {/* Left Column - Invoice Details */}
          <Col md={8}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaFileInvoice className="me-2" />
                  {isEditing ? t('facture.editInvoice') : t('facture.newInvoice')}
                  {isEditing && invoice?.numero && (
                    <Badge color="info" className="ms-2">
                      {invoice.numero}
                    </Badge>
                  )}
                </h5>
                <div>
                  {isEditing && invoice?.statut && (
                    <Badge
                      color={
                        invoice.statut === 'PAYEE' ? 'success' :
                          invoice.statut === 'ANNULEE' ? 'danger' :
                            invoice.statut === 'PARTIELLEMENT_PAYEE' ? 'warning' :
                              'secondary'
                      }
                      className="me-2"
                    >
                      {t(`facture.${invoice.statut.toLowerCase()}`)}
                    </Badge>
                  )}
                  {canBeUpdated === false && (
                    <Badge color="warning">
                      {t('facture.readOnly')}
                    </Badge>
                  )}
                  {formik.values.fromPayments && (
                    <Badge color="info">
                      {t('facture.fromPayments')}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {/* Display selected payments info if creating from payments */}
                {formik.values.fromPayments && selectedPaymentsForInvoice.length > 0 && (
                  <Card className="mb-3 border-info">
                    <CardHeader className="bg-info text-white">
                      <h6 className="mb-0 d-flex align-items-center">
                        <FaMoneyBillWave className="me-2" />
                        {t('facture.selectedPayments')}
                        <Badge color="light" className="ms-2 text-dark">
                          {selectedPaymentsForInvoice.length}
                        </Badge>
                      </h6>
                    </CardHeader>
                    <CardBody className="p-3">
                      <div className="table-responsive">
                        <Table size="sm" bordered>
                          <thead className="bg-light">
                            <tr>
                              <th>{t('payment.reference')}</th>
                              <th>{t('payment.date')}</th>
                              <th>{t('payment.mode')}</th>
                              <th>{t('payment.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPaymentsForInvoice.map(payment => (
                              <tr key={payment.id}>
                                <td>{payment.reference || '-'}</td>
                                <td>{payment.dateReglement}</td>
                                <td>{getPaymentModeLabel(payment.modeReglement)}</td>
                                <td className="text-end">{formatPrice(payment.montantRegle)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-light">
                            <tr>
                              <td colSpan="3" className="text-end fw-bold">{t('payment.total')}:</td>
                              <td className="text-end fw-bold">{formatPrice(calculateSelectedPaymentsTotal())}</td>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Section des références de bons de livraison */}
                {isEditing && invoice?.bonLivraionsRefs && (
                  <Card className="mb-3 border-info">
                    <CardHeader className="bg-info text-white">
                      <h6 className="mb-0 d-flex align-items-center">
                        <FaTruck className="me-2" />
                        {t('facture.deliveryReferences')}
                        <Badge color="light" className="ms-2 text-dark">
                          {parseBonLivraisonRefs(invoice.bonLivraionsRefs).length}
                        </Badge>
                      </h6>
                    </CardHeader>
                    <CardBody className="p-1">
                      <div className="row">
                        {parseBonLivraisonRefs(invoice.bonLivraionsRefs).map((ref, index) => (
                          <div key={index} className="col-md-6 col-lg-4 mb-2">
                            <div className="d-flex align-items-center p-2 bg-light rounded">
                              <FaTruck className="text-info me-2" size={14} />
                              <span className="fw-bold text-primary">{ref}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                )}
                <Row>
                  <Col md={4}>
                      <FormGroup>
                        <Label for="dateEcheanceCheque">{t('facture.client')}</Label>
                        <Input
                          id="clientNom"
                          name="clientNom"
                          type="text"
                          value={`${formik.values.clientCode} / ${formik.values.clientNom}` || ''}
                          disabled
                        />
                      </FormGroup>
                    </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="dateFacture">
                        <FaCalendarAlt className="me-1" />
                        {t('facture.dateFacture')} *
                      </Label>
                      <Input
                        type="date"
                        id="dateFacture"
                        name="dateFacture"
                        value={formik.values.dateFacture}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!canBeUpdated}
                        invalid={formik.touched.dateFacture && formik.errors.dateFacture}
                      />
                      {formik.touched.dateFacture && formik.errors.dateFacture && (
                        <div className="invalid-feedback">{formik.errors.dateFacture}</div>
                      )}
                    </FormGroup>
                  </Col>
                
                  {/* <Col md={6}>
                    <FormGroup>
                      <Label for="dateEcheance">
                        <FaCalendarAlt className="me-1" />
                        {t('facture.dateEcheance')}
                      </Label>
                      <Input
                        type="date"
                        id="dateEcheance"
                        name="dateEcheance"
                        value={formik.values.dateEcheance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.dateEcheance && formik.errors.dateEcheance}
                        min={formik.values.dateFacture}
                      />
                      {formik.touched.dateEcheance && formik.errors.dateEcheance && (
                        <div className="invalid-feedback">{formik.errors.dateEcheance}</div>
                      )}
                    </FormGroup>
                  </Col> */}
                 <Col md={3}>
                    <FormGroup>
                      <Label for="modeReglement">
                        <FaMoneyBillWave className="me-1" />
                        {t('facture.modeReglement')} *
                      </Label>
                      <Input
                        type="select"
                        id="modeReglement"
                        name="modeReglement"
                        value={formik.values.modeReglement}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!canBeUpdated}
                        invalid={formik.touched.modeReglement && formik.errors.modeReglement}
                      >
                        <option value="">{t('common.select')}</option>
                        <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                        <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                        <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                        <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                        <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                      </Input>
                      {formik.touched.modeReglement && formik.errors.modeReglement && (
                        <div className="invalid-feedback">{formik.errors.modeReglement}</div>
                      )}
                    </FormGroup>
                  </Col>
                   {/* {formik.values.modeReglement === 'CHEQUE' && (<>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="numeroCheque">{t('expense.numeroCheque')}</Label>
                        <Input
                          id="numeroCheque"
                          name="numeroCheque"
                          value={formik.values.numeroCheque || ''}
                          onChange={formik.handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="banque">{t('expense.banque')}</Label>
                        <Input
                          type="select"
                          id="banque"
                          name="banque"
                          value={formik.values.banque}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.banque && formik.errors.banque}
                        >
                          <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                          <option value="BMCE">BMCE</option>
                          <option value="ATTIJARI">ATTIJARI</option>
                          <option value="CIH">CIH</option>
                          <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                          <option value="SG">SG</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="dateEcheanceCheque">{t('facture.dateEcheance')}</Label>
                        <Input
                          id="dateEcheanceCheque"
                          name="dateEcheanceCheque"
                          type="date"
                          value={formik.values.dateEcheanceCheque || ''}
                          onChange={formik.handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </>
                  )} */}
                </Row>

                {/* Observations */}
                <FormGroup>
                  <Label for="commentaire">
                    {t('facture.commentaire')}
                  </Label>
                  <Input
                    type="textarea"
                    id="commentaire"
                    name="commentaire"
                    rows="3"
                    value={formik.values.commentaire}
                    onChange={formik.handleChange}
                    disabled={!canBeUpdated}
                    onBlur={formik.handleBlur}
                    placeholder={t('facture.commentairePlaceholder')}
                  />
                </FormGroup>
              </CardBody>
            </Card>

            {/* Products Section */}
            <Card className="shadow-sm">
              <CardHeader className="bg-secondary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaBarcode className="me-2" />
                  {t('facture.articles')}
                </h5>
                {/* <Button
                  type="button"
                  color="success"
                  size="sm"
                  onClick={() => setShowProductSearch(true)}
                >
                  <FaPlus className="me-1" />
                  {t('facture.addArticle')}
                </Button> */}
              </CardHeader>
              <CardBody>
                {formik.values.lignes.length === 0 ? (
                  <Alert color="info" className="text-center">
                    <FaBarcode size={48} className="mb-3 d-block mx-auto opacity-50" />
                    {t('facture.noArticles')}
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '100px' }}>{t('facture.code')}</th>
                          <th>{t('facture.designation')}</th>
                          <th style={{ width: '80px' }}>{t('facture.quantite')}</th>
                          <th style={{ width: '80px' }}>{t('facture.unite')}</th>
                          <th style={{ width: '100px' }}>{t('facture.prixUnitaireTTC')}</th>
                          <th style={{ width: '80px' }}>{t('facture.tauxTVA')}</th>
                          <th style={{ width: '100px' }}>{t('facture.montantHT')}</th>
                          <th style={{ width: '100px' }}>{t('facture.montantTTC')}</th>
                          {/* <th style={{ width: '50px' }}></th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.lignes.map((ligne, index) => (
                          <tr key={index}>
                            <td>
                              <Input
                                type="text"
                                value={ligne.articleCode}
                                onChange={(e) => handleLineFieldChange(index, 'articleCode', e.target.value)}
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={ligne.articleNom}
                                onChange={(e) => handleLineFieldChange(index, 'articleNom', e.target.value)}
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                value={ligne.quantite}
                                onChange={(e) => handleLineFieldChange(index, 'quantite', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <span className="small">{getUniteLabel(ligne.unite)}</span>
                            </td>
                            <td>
                              <Input
                                type="number"
                                value={ligne.prixUnitaireTTC}
                                onChange={(e) => handleLineFieldChange(index, 'prixUnitaireTTC', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                size="sm"
                                disabled
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                value={(ligne.tauxTVA * 100).toFixed(0)}
                                onChange={(e) => handleLineFieldChange(index, 'tauxTVA', (parseFloat(e.target.value) || 0) / 100)}
                                min="0"
                                max="100"
                                step="1"
                                size="sm"
                                disabled
                              />
                            </td>
                            <td className="text-end">
                              {formatPrice(ligne.montantHT, false)}
                            </td>
                            <td className="text-end">
                              {formatPrice(ligne.montantTTC, false)}
                            </td>
                            {/* <td>
                              <Button
                                type="button"
                                color="danger"
                                size="sm"
                                onClick={() => handleRemoveLine(index)}
                                title={t('facture.removeLine')}
                              >
                                <FaTrash size={12} />
                              </Button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {formik.touched.lignes && formik.errors.lignes && (
                  <Alert color="danger" className="mt-2">
                    {formik.errors.lignes}
                  </Alert>
                )}
              </CardBody>
            </Card>
            {/* Payments Section - Only show for existing invoices */}
            {isEditing && invoice?.id && (
              <Card className="shadow-sm mt-4">
                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaMoneyBillWave className="me-2" />
                    {t('payment.payments')}
                  </h5>

                </CardHeader>
                <CardBody>
                  {reglementsLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <div className="mt-2 text-muted">{t('common.loading')}</div>
                    </div>
                  ) : invoicePayments.length === 0 ? (
                    <Alert color="info" className="text-center">
                      <FaMoneyBillWave size={48} className="mb-3 d-block mx-auto opacity-50" />
                      {t('payment.noPayments')}
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead className="bg-light">
                          <tr>
                            <th>{t('payment.reference')}</th>
                            <th>{t('payment.date')}</th>
                            <th>{t('payment.mode')}</th>
                            <th>{t('payment.piece')}</th>
                            <th>{t('payment.banque')}</th>
                            <th>{t('payment.dateEcheance')}</th>
                            <th>{t('payment.amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoicePayments.map((payment) => (
                            <tr key={payment.id}>
                              <td>{payment.refReglement || '-'}</td>
                              <td>{payment.createdAt}</td>
                              <td>{getPaymentModeLabel(payment.modeReglement)}</td>
                              <td>{payment.numeroCheque || payment.referencePaiement || '-'}</td>
                              <td>{payment.banque || '-'}</td>
                              <td>{payment.dateEcheance || '-'}</td>
                              <td className="text-end fw-bold">{formatPrice(payment.montantAlloue, false)}</td>

                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-light">
                          <tr>
                            <td colSpan="6" className="text-end fw-bold">{t('payment.totalPaid')}:</td>
                            <td className="text-end fw-bold">{formatPrice(calculateTotalPaid(), false)}</td>
                          </tr>
                          <tr>
                            <td colSpan="6" className="text-end fw-bold">{t('payment.remainingAmount')}:</td>

                            <td className="text-end">
                              <span className={`fw-bold ${calculateRemainingAmount() > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatPrice(calculateRemainingAmount(), false)}
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
            {/* Payments from Reglements Section - Add this new section */}
            {stateFromPayments && paymentsForInvoice.length > 0 && (
              <Card className="shadow-sm mt-4">
                <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaMoneyBillWave className="me-2" />
                    {t('facture.paymentsFromReglements')}
                  </h5>
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    onClick={() => setShowPaymentsDetails(!showPaymentsDetails)}
                  >
                    {showPaymentsDetails ? t('common.hide') : t('common.show')}
                  </Button>
                </CardHeader>
                <CardBody>
                  <Alert color="info" className="mb-3">
                    <FaInfoCircle className="me-2" />
                    {t('facture.paymentsFromReglementsInfo')}
                  </Alert>

                  {showPaymentsDetails && (
                    <div className="table-responsive">
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>{t('reglement.reference')}</th>
                            <th>{t('reglement.date')}</th>
                            <th>{t('reglement.modeReglement')}</th>
                            <th>{t('reglement.montant')}</th>
                            <th>{t('reglement.statut')}</th>
                            <th style={{ width: '50px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsForInvoice.map(payment => (
                            <tr key={payment.id}>
                              <td>{payment.reference || '-'}</td>
                              <td>{payment.dateReglement}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{getPaymentModeIcon(payment.modeReglement)}</span>
                                  <span>{getPaymentModeLabel(payment.modeReglement)}</span>
                                </div>
                                {payment.modeReglement === 'CHEQUE' && payment.referencePaiement && (
                                  <small className="text-muted d-block">
                                    {payment.referencePaiement} - {payment.banque || ''}
                                  </small>
                                )}
                              </td>
                              <td className="text-end fw-bold">{formatPrice(payment.montantRegle, false)}</td>
                              <td>{getPaymentStatusBadge(payment.statut)}</td>
                              <td>
                                <Button
                                  type="button"
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemovePayment(payment.id)}
                                  title={t('common.remove')}
                                >
                                  <FaTrash size={12} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3" className="text-end fw-bold">{t('reglement.total')}:</td>
                            <td className="text-end fw-bold text-success">{formatPrice(calculatePaymentsTotal(), false)}</td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Right Column - Summary */}
          <Col md={4}>
            <Card className="shadow-sm sticky-top" style={{ top: '20px' }}>
              <CardHeader className="bg-info text-white">
                <h5 className="mb-0">
                  <FaCalculator className="me-2" />
                  {t('facture.summary')}
                </h5>
              </CardHeader>
              <CardBody>
                {/* Totals Summary */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">{t('facture.totalHT')}:</span>
                    <span className="fw-bold">{formatPrice(formik.values.montantTotalHT)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">{t('facture.totalTVA')}:</span>
                    <span className="fw-bold">{formatPrice(formik.values.montantTotalTaxe)}</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-bold text-primary">{t('facture.totalTTC')}:</span>
                    <span className="fw-bold text-primary fs-5">{formatPrice(formik.values.montantTotalTTC)}</span>
                  </div>
                </div>

                {/* Payment Information */}
                {isEditing && invoice?.id && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">{t('payment.totalPaid')}:</span>
                      <span className="fw-bold text-success">{formatPrice(calculateTotalPaid())}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">{t('payment.remainingAmount')}:</span>
                      <span className={`fw-bold ${calculateRemainingAmount() > 0 ? 'text-danger' : 'text-success'}`}>
                        {formatPrice(calculateRemainingAmount())}
                      </span>
                    </div>
                  </div>
                )}

                {/* Payment Information for new invoices */}
                {!isEditing && !formik.values.fromPayments && (
                  <div className="mb-4">
                    <FormGroup>
                      <Label for="montantRegle">
                        <FaMoneyBillWave className="me-1" />
                        {t('facture.montantRegle')}
                      </Label>
                      <Input
                        type="number"
                        onFocus={(e) => e.target.select()} // Select the field content on focus
                        id="montantRegle"
                        name="montantRegle"
                        value={formik.values.montantRegle}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        min="0"
                        step="0.01"
                        max={formik.values.montantTotalTTC}
                      />
                    </FormGroup>

                    {/* Remaining Amount */}
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">{t('facture.montantRestant')}:</span>
                      <span className={`fw-bold ${(formik.values.montantTotalTTC - formik.values.montantRegle) > 0
                        ? 'text-danger'
                        : 'text-success'
                        }`}>
                        {formatPrice(formik.values.montantTotalTTC - formik.values.montantRegle)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Status */}
                {/* <div className="mb-4">
                  <FormGroup>
                    <Label for="statut">
                      {t('facture.status')}
                    </Label>
                    <Input
                      type="select"
                      id="statut"
                      name="statut"
                      value={formik.values.statut}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={formik.values.fromPayments} // Disable if creating from payments
                    >
                      <option value="VALIDEE">{t('facture.validated')}</option>
                      <option value="IMPAYE">{t('facture.encours')}</option>
                      <option value="PAYEE">{t('facture.paid')}</option>
                      <option value="PARTIELLEMENT_PAYEE">{t('facture.partiallyPaid')}</option>
                      <option value="ANNULEE">{t('facture.cancelled')}</option>
                    </Input>
                  </FormGroup>
                </div> */}

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                
                  <Button
                    color="primary"
                    type="submit"
                    disabled={!canBeUpdated}
                  >
                    {invoiceLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {isEditing ? t('common.updating') : t('common.saving')}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {isEditing ? t('common.editInvoice') : t('common.save')}
                      </>
                    )}
                  </Button>
                </div>

                {/* Invoice Statistics */}
                <div className="mt-4 pt-3 border-top">
                  <small className="text-muted d-block mb-2">{t('facture.statistics')}:</small>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">{t('facture.totalLines')}:</small>
                    <small className="fw-bold">{formik.values.lignes.length}</small>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">{t('facture.totalQuantity')}:</small>
                    <small className="fw-bold">
                      {formik.values.lignes.reduce((sum, ligne) => sum + (ligne.quantite || 0), 0).toFixed(2)}
                    </small>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default ViewFacture;


